// Strict allow-list SVG sanitiser for AI-generated logos.
//
// Used by the ai-generate-logo edge function before anything is returned, and
// again in the browser before a logo is previewed or uploaded (defence in
// depth). Pure TypeScript with no Deno / DOM APIs so it runs in Deno, the
// browser and the Playwright unit tests (tests/svg-sanitize.spec.ts).
//
// It never "cleans up" markup in place. The input is parsed into a tree by a
// small strict XML parser, and a brand-new document is written out from that
// tree using only the elements and attributes allowed below:
//   * Elements: svg, g, path, circle, ellipse, rect, line, polyline, polygon,
//     text, tspan, defs, linearGradient, radialGradient, stop, clipPath, title.
//     Anything else (script, style, foreignObject, image, use, a, filter,
//     animate, set …) is dropped together with everything inside it.
//   * Attributes: presentation and geometry attributes only. No on*, no
//     href / xlink:href, no style, no class, no other namespaces.
//   * url(...) values are only kept when they point at an id in this document
//     (url(#grad1)). javascript:, data:, vbscript: and CSS expression() make
//     the attribute disappear.
//   * DOCTYPE / ENTITY / CDATA make the whole document fail (no entity tricks).
//   * A viewBox is required (derived from numeric width/height if missing),
//     and input and output are capped at MAX_SVG_BYTES.

export const MAX_SVG_BYTES = 50 * 1024;
const MAX_DEPTH = 32;
const MAX_ELEMENTS = 3000;
const SVG_NS = "http://www.w3.org/2000/svg";

export const ALLOWED_ELEMENTS = new Set([
  "svg", "g", "path", "circle", "ellipse", "rect", "line", "polyline", "polygon",
  "text", "tspan", "defs", "linearGradient", "radialGradient", "stop", "clipPath", "title",
]);

/** Elements whose character data is kept. */
const TEXT_ELEMENTS = new Set(["text", "tspan", "title"]);

const PRESENTATION_ATTRS = [
  "id", "transform", "fill", "fill-opacity", "fill-rule", "stroke", "stroke-width", "stroke-opacity",
  "stroke-linecap", "stroke-linejoin", "stroke-miterlimit", "stroke-dasharray", "stroke-dashoffset",
  "opacity", "clip-path", "clip-rule", "font-family", "font-size", "font-weight", "font-style",
  "letter-spacing", "word-spacing", "text-anchor", "dominant-baseline", "alignment-baseline",
  "text-rendering", "shape-rendering", "paint-order", "visibility", "vector-effect", "color",
];

const ELEMENT_ATTRS: Record<string, string[]> = {
  svg: ["viewBox", "width", "height", "preserveAspectRatio", "role", "aria-label"],
  g: [],
  path: ["d", "pathLength"],
  circle: ["cx", "cy", "r"],
  ellipse: ["cx", "cy", "rx", "ry"],
  rect: ["x", "y", "width", "height", "rx", "ry"],
  line: ["x1", "y1", "x2", "y2"],
  polyline: ["points"],
  polygon: ["points"],
  text: ["x", "y", "dx", "dy", "rotate", "textLength", "lengthAdjust"],
  tspan: ["x", "y", "dx", "dy", "rotate", "textLength", "lengthAdjust"],
  defs: [],
  linearGradient: ["x1", "y1", "x2", "y2", "gradientUnits", "gradientTransform", "spreadMethod"],
  radialGradient: ["cx", "cy", "r", "fx", "fy", "fr", "gradientUnits", "gradientTransform", "spreadMethod"],
  stop: ["offset", "stop-color", "stop-opacity"],
  clipPath: ["clipPathUnits"],
  title: [],
};

const ALLOWED_ATTRS: Record<string, Set<string>> = Object.fromEntries(
  Object.entries(ELEMENT_ATTRS).map(([el, attrs]) => [el, new Set([...PRESENTATION_ATTRS, ...attrs])]),
);

export type SanitizeResult =
  | { ok: true; svg: string; width: number; height: number; removed: string[] }
  | { ok: false; reason: string };

interface ElementNode {
  type: "element";
  name: string;
  attrs: Array<[string, string]>;
  children: Node[];
}
interface TextNode {
  type: "text";
  value: string;
}
type Node = ElementNode | TextNode;

class SvgParseError extends Error {}

const byteLength = (value: string) => new TextEncoder().encode(value).length;

const NAMED_ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };

function decodeEntities(value: string): string {
  return value.replace(/&(#x[0-9a-fA-F]{1,6}|#[0-9]{1,7}|[A-Za-z]+);?/g, (match, ref: string) => {
    if (!match.endsWith(";")) throw new SvgParseError("Malformed character reference");
    if (ref[0] === "#") {
      const code = ref[1] === "x" || ref[1] === "X" ? Number.parseInt(ref.slice(2), 16) : Number.parseInt(ref.slice(1), 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) {
        throw new SvgParseError("Invalid character reference");
      }
      return String.fromCodePoint(code);
    }
    const named = NAMED_ENTITIES[ref];
    if (named === undefined) throw new SvgParseError(`Unknown entity &${ref};`);
    return named;
  });
}

const escapeText = (value: string) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escapeAttr = (value: string) => escapeText(value).replace(/"/g, "&quot;");

const NAME_RE = /^[A-Za-z_][\w:.-]*/;

/** Strict, small XML parser: elements, attributes, text, comments, PIs. */
function parse(input: string): ElementNode {
  const root: ElementNode = { type: "element", name: "#root", attrs: [], children: [] };
  const stack: ElementNode[] = [root];
  let i = 0;
  let elementCount = 0;

  while (i < input.length) {
    const lt = input.indexOf("<", i);
    if (lt === -1) {
      stack[stack.length - 1].children.push({ type: "text", value: decodeEntities(input.slice(i)) });
      break;
    }
    if (lt > i) stack[stack.length - 1].children.push({ type: "text", value: decodeEntities(input.slice(i, lt)) });
    i = lt;

    if (input.startsWith("<!--", i)) {
      const end = input.indexOf("-->", i + 4);
      if (end === -1) throw new SvgParseError("Unclosed comment");
      i = end + 3;
      continue;
    }
    if (input.startsWith("<?", i)) {
      const end = input.indexOf("?>", i + 2);
      if (end === -1) throw new SvgParseError("Unclosed processing instruction");
      i = end + 2;
      continue;
    }
    if (input.startsWith("<!", i)) {
      // DOCTYPE, ENTITY, CDATA … never needed for a logo and the usual route
      // for entity-expansion and parser-confusion tricks.
      throw new SvgParseError("DOCTYPE, ENTITY and CDATA sections are not allowed");
    }
    if (input.startsWith("</", i)) {
      const nameMatch = NAME_RE.exec(input.slice(i + 2));
      if (!nameMatch) throw new SvgParseError("Malformed closing tag");
      const name = nameMatch[0];
      let j = i + 2 + name.length;
      while (j < input.length && /\s/.test(input[j])) j++;
      if (input[j] !== ">") throw new SvgParseError("Malformed closing tag");
      const open = stack.pop();
      if (!open || open === root || open.name !== name) throw new SvgParseError(`Mismatched closing tag </${name}>`);
      i = j + 1;
      continue;
    }

    // Opening tag.
    const nameMatch = NAME_RE.exec(input.slice(i + 1));
    if (!nameMatch) throw new SvgParseError("Malformed tag");
    const name = nameMatch[0];
    let j = i + 1 + name.length;
    const attrs: Array<[string, string]> = [];
    let selfClosing = false;
    for (;;) {
      const ws = j;
      while (j < input.length && /\s/.test(input[j])) j++;
      if (j >= input.length) throw new SvgParseError("Unclosed tag");
      if (input[j] === ">") {
        j++;
        break;
      }
      if (input.startsWith("/>", j)) {
        selfClosing = true;
        j += 2;
        break;
      }
      if (j === ws) throw new SvgParseError("Attributes must be separated by whitespace");
      const attrMatch = NAME_RE.exec(input.slice(j));
      if (!attrMatch) throw new SvgParseError("Malformed attribute");
      const attrName = attrMatch[0];
      j += attrName.length;
      while (j < input.length && /\s/.test(input[j])) j++;
      if (input[j] !== "=") throw new SvgParseError(`Attribute ${attrName} has no value`);
      j++;
      while (j < input.length && /\s/.test(input[j])) j++;
      const quote = input[j];
      if (quote !== '"' && quote !== "'") throw new SvgParseError("Attribute values must be quoted");
      const close = input.indexOf(quote, j + 1);
      if (close === -1) throw new SvgParseError("Unclosed attribute value");
      const raw = input.slice(j + 1, close);
      if (raw.includes("<")) throw new SvgParseError("'<' is not allowed in attribute values");
      if (attrs.some(([n]) => n === attrName)) throw new SvgParseError(`Duplicate attribute ${attrName}`);
      attrs.push([attrName, decodeEntities(raw)]);
      j = close + 1;
    }

    elementCount++;
    if (elementCount > MAX_ELEMENTS) throw new SvgParseError("Too many elements");
    const node: ElementNode = { type: "element", name, attrs, children: [] };
    stack[stack.length - 1].children.push(node);
    if (!selfClosing) {
      stack.push(node);
      if (stack.length - 1 > MAX_DEPTH) throw new SvgParseError("Nested too deeply");
    }
    i = j;
  }

  if (stack.length !== 1) throw new SvgParseError(`Unclosed <${stack[stack.length - 1].name}>`);
  return root;
}

const DANGEROUS_VALUE = /(javascript|vbscript|data|file|livescript)\s*:|expression\s*\(|@import|\\/i;
const URL_REF = /url\(([^)]*)\)/gi;
const LOCAL_URL = /^\s*['"]?#[A-Za-z][\w.-]{0,63}['"]?\s*$/;
const ID_RE = /^[A-Za-z][\w.-]{0,63}$/;
const NUMBER_RE = /^\s*-?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?\s*(px)?\s*$/i;

/** Normalise and validate one attribute value; null drops the attribute. */
function cleanAttrValue(name: string, value: string): string | null {
  // Control characters (other than whitespace) have no business in a logo.
  // eslint-disable-next-line no-control-regex
  const v = value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, "");
  if (v.length > (name === "d" || name === "points" ? 20000 : 500)) return null;
  // Strip whitespace/control obfuscation before looking for schemes ("java\tscript:").
  if (DANGEROUS_VALUE.test(v) || DANGEROUS_VALUE.test(v.replace(/\s+/g, ""))) return null;
  if (name === "id" && !ID_RE.test(v)) return null;
  const urls = [...v.matchAll(URL_REF)];
  if (urls.some((m) => !LOCAL_URL.test(m[1]))) return null;
  // A "url(" that the pattern above couldn't match (e.g. unbalanced) is suspicious.
  if (/url\s*\(/i.test(v) && urls.length === 0) return null;
  return v;
}

function parseViewBox(value: string | undefined): [number, number, number, number] | null {
  if (!value) return null;
  const parts = value.trim().split(/[\s,]+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  if (parts[2] <= 0 || parts[3] <= 0 || parts[2] > 100000 || parts[3] > 100000) return null;
  return parts as [number, number, number, number];
}

const trimNumber = (n: number) => String(Math.round(n * 1000) / 1000);

/** Sanitise untrusted SVG markup. Never throws. */
export function sanitizeSvg(input: unknown): SanitizeResult {
  if (typeof input !== "string" || !input.trim()) return { ok: false, reason: "No SVG markup" };
  if (byteLength(input) > MAX_SVG_BYTES) return { ok: false, reason: "SVG is too large" };

  let tree: ElementNode;
  try {
    tree = parse(input.replace(/^\uFEFF/, ""));
  } catch (error) {
    return { ok: false, reason: error instanceof Error ? error.message : "Could not read the SVG" };
  }

  const roots = tree.children.filter((c): c is ElementNode => c.type === "element");
  if (tree.children.some((c) => c.type === "text" && c.value.trim())) {
    return { ok: false, reason: "Unexpected text outside the <svg> element" };
  }
  if (roots.length !== 1 || roots[0].name !== "svg") return { ok: false, reason: "The document must be a single <svg> element" };
  const svgRoot = roots[0];

  const removed = new Set<string>();
  const ids = new Set<string>();

  const writeAttrs = (node: ElementNode): Array<[string, string]> => {
    const allowed = ALLOWED_ATTRS[node.name];
    const out: Array<[string, string]> = [];
    for (const [name, value] of node.attrs) {
      if (name === "xmlns") continue; // rewritten on the root
      if (!allowed.has(name)) {
        removed.add(`@${name}`);
        continue;
      }
      const clean = cleanAttrValue(name, value);
      if (clean === null) {
        removed.add(`@${name}`);
        continue;
      }
      if (name === "id") {
        if (ids.has(clean)) {
          removed.add("@id");
          continue;
        }
        ids.add(clean);
      }
      out.push([name, clean]);
    }
    return out;
  };

  const serialise = (node: ElementNode, attrs: Array<[string, string]>): string => {
    const attrText = attrs.map(([n, v]) => ` ${n}="${escapeAttr(v)}"`).join("");
    const children = node.children
      .map((child) => {
        if (child.type === "text") return TEXT_ELEMENTS.has(node.name) ? escapeText(child.value) : "";
        if (!ALLOWED_ELEMENTS.has(child.name) || child.name === "svg") {
          // Nested <svg> is dropped too: one viewport, one viewBox.
          removed.add(child.name);
          return "";
        }
        return serialise(child, writeAttrs(child));
      })
      .join("");
    return `<${node.name}${attrText}>${children}</${node.name}>`;
  };

  // Root: force the SVG namespace and a usable viewBox / size.
  const rootAttrs = writeAttrs(svgRoot).filter(([n]) => n !== "viewBox" && n !== "width" && n !== "height");
  const original = Object.fromEntries(svgRoot.attrs);
  let viewBox = parseViewBox(original.viewBox);
  if (!viewBox && NUMBER_RE.test(original.width ?? "") && NUMBER_RE.test(original.height ?? "")) {
    const w = Number.parseFloat(original.width);
    const h = Number.parseFloat(original.height);
    if (w > 0 && h > 0 && w <= 100000 && h <= 100000) viewBox = [0, 0, w, h];
  }
  if (!viewBox) return { ok: false, reason: "The SVG needs a viewBox" };
  const [, , vbW, vbH] = viewBox;
  const head: Array<[string, string]> = [
    ["xmlns", SVG_NS],
    ["viewBox", viewBox.map(trimNumber).join(" ")],
    ["width", trimNumber(vbW)],
    ["height", trimNumber(vbH)],
    ...rootAttrs,
  ];

  const svg = serialise(svgRoot, head);
  if (byteLength(svg) > MAX_SVG_BYTES) return { ok: false, reason: "SVG is too large" };
  if (!/<(path|circle|ellipse|rect|line|polyline|polygon|text)\b/.test(svg)) {
    return { ok: false, reason: "The SVG has nothing to draw" };
  }
  return { ok: true, svg, width: vbW, height: vbH, removed: [...removed].sort() };
}

/** `data:` URL for an <img src>, from markup that has already been sanitised. */
export function svgDataUrl(sanitisedSvg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sanitisedSvg)}`;
}
